// Pure derivation for the CRM Command Center (no React, no network).
// Mirrors the brainSignals.mjs pattern: the screen renders, this file thinks.
//
//   leadRules         — every urgency rule a single lead triggers (overdue
//                       follow-up, closing window, idle, hot-no-step, …).
//   buildBlips        — radar layout. Leads are ranked by urgency into three
//                       priority bands (NOW / NEXT / WATCH) and placed on a
//                       golden-angle spiral inside each band, so thousands of
//                       leads never overlap and the disc fills evenly.
//                       Radial position = when to act; colour = why.
//   buildMissionQueue — the guided-advice queue (top rule per lead + meeting
//                       prep + org-level growth advisories).
//   healthScore       — 0..100 mix of win rate, freshness, follow-up
//                       discipline. Drives the core's colour.
//
// Run `node commandSignals.check.mjs` for the self-check.

const DAY = 86_400_000;
const GOLDEN_DEG = 137.50776405; // golden angle — consecutive points never align

export const ACTIVE_STATUSES = ["open", "hot", "warm", "cold"];

export const idleDaysOf = (lead, now) =>
  Math.max(0, Math.floor((now - new Date(lead.updatedAt || lead.createdAt || now).getTime()) / DAY));

/* The moment a pending follow-up actually comes due, snooze included. 0 when
   the lead has no pending follow-up. Every "what's next" surface reads this so
   the row, the drawer and the queue can never disagree on the due time. */
export const dueAtOf = (lead) => {
  const fu = lead.followUp;
  if (!fu || fu.status !== "pending" || !fu.scheduledAt) return 0;
  const snoozed = fu.snoozedUntil ? new Date(fu.snoozedUntil).getTime() : 0;
  return Math.max(new Date(fu.scheduledAt).getTime(), snoozed);
};

// Sign-carrying gap to a due moment: "in 45m", "in 3h", "2d overdue".
export const dueLabel = (at, now) => {
  const diff = at - now;
  const abs = Math.abs(diff);
  const n = abs < 3_600_000
    ? `${Math.max(1, Math.round(abs / 60_000))}m`
    : abs < DAY
      ? `${Math.round(abs / 3_600_000)}h`
      : `${Math.max(1, Math.round(abs / DAY))}d`;
  return diff >= 0 ? `in ${n}` : `${n} overdue`;
};

// Overdue = pending follow-up whose (snooze-adjusted) time is in the past.
export const isOverdue = (lead, now) => {
  const at = dueAtOf(lead);
  return at > 0 && at < now;
};

const isDueSoon = (lead, now) => {
  const at = dueAtOf(lead);
  return at > 0 && at >= now && at - now <= DAY;
};

const activeOf = (leads) => (leads || []).filter((l) => ACTIVE_STATUSES.includes(l.status));

/* Every urgency rule a lead triggers, heaviest first. valNorm 0..1 lets big
   deals outrank small ones inside the same rule. fmtValue renders currency. */
export function leadRules(l, now, { valNorm = 0, fmtValue = (v) => String(v) } = {}) {
  const idle = idleDaysOf(l, now);
  const val = l.value ? ` · ${fmtValue(l.value)}` : "";
  const rules = [];

  if (isOverdue(l, now)) {
    const at = dueAtOf(l);
    const over = Math.max(1, Math.floor((now - at) / DAY));
    rules.push({ severity: "critical", kind: "followup-overdue", weight: 1000 + Math.min(over, 30) * 8 + valNorm * 40, title: `Follow up: ${l.title}`, evidence: `Overdue by ${over}d${val}`, to: "followups" });
  }
  if (isDueSoon(l, now)) {
    // "within 24h" left people guessing whether to act now or after lunch.
    rules.push({ severity: "high", kind: "followup-today", weight: 820 + valNorm * 40, title: `Follow-up due: ${l.title}`, evidence: `Due ${dueLabel(dueAtOf(l), now)}${val}`, to: "followups" });
  }
  if (l.expectedCloseDate) {
    const dd = Math.floor((new Date(l.expectedCloseDate).getTime() - now) / DAY);
    if (dd < 0) rules.push({ severity: "high", kind: "close-passed", weight: 760 + valNorm * 60, title: `Close it or move it: ${l.title}`, evidence: `Expected close ${-dd}d ago${val}`, to: "pipeline" });
    else if (dd <= 7) rules.push({ severity: "high", kind: "close-window", weight: 640 + valNorm * 60, title: `Closing window: ${l.title}`, evidence: `Expected close in ${dd}d${val}`, to: "pipeline" });
  }
  if (l.status === "hot" && (!l.followUp || l.followUp.status !== "pending")) {
    rules.push({ severity: "high", kind: "hot-no-step", weight: 560 + valNorm * 60, title: `Hot deal, no next step: ${l.title}`, evidence: `Schedule a follow-up${val}`, to: "pipeline" });
  }
  if (idle >= 14) rules.push({ severity: "high", kind: "idle", weight: 520 + Math.min(idle, 45) + valNorm * 40, title: `Re-engage: ${l.title}`, evidence: `Untouched for ${idle}d${val}`, to: "pipeline", idleFilter: "14" });
  else if (idle >= 7) rules.push({ severity: "medium", kind: "idle", weight: 440 + idle + valNorm * 40, title: `Nudge: ${l.title}`, evidence: `Untouched for ${idle}d${val}`, to: "pipeline", idleFilter: "7" });
  if (l.status === "open" && now - new Date(l.createdAt || now).getTime() <= 2 * DAY && (!l.followUp || l.followUp.status !== "pending")) {
    rules.push({ severity: "medium", kind: "qualify", weight: 400 + valNorm * 30, title: `Qualify new lead: ${l.title}`, evidence: `Fresh lead — set temperature + follow-up${val}`, to: "leads" });
  }
  return rules.sort((a, b) => b.weight - a.weight);
}

/* ── Radar bands. Priority decides the ring, urgency decides the colour. ── */
export const BANDS = [
  { key: "now", label: "NOW", cap: 28 },
  { key: "next", label: "NEXT", cap: 52 },
  { key: "watch", label: "WATCH", cap: 96 },
];

const bandOf = (topRule) => {
  if (!topRule) return "watch";
  if (topRule.severity === "critical" || topRule.severity === "high") return "now";
  if (topRule.severity === "medium") return "next";
  return "watch";
};

/* Golden-angle spiral per band: point i of n sits at r01 = sqrt((i+.5)/n)
   (area-uniform, no clumping) and angle i·137.507° (+ band phase so bands
   don't form radial spokes). Deterministic, collision-free by construction. */
export function buildBlips(leads, now) {
  const active = activeOf(leads);
  const maxValue = active.reduce((m, l) => Math.max(m, l.value || 0), 0);

  const grouped = { now: [], next: [], watch: [] };
  for (const l of active) {
    const top = leadRules(l, now, { valNorm: maxValue > 0 ? (l.value || 0) / maxValue : 0 })[0] || null;
    grouped[bandOf(top)].push({ lead: l, top });
  }

  let shown = 0;
  const bands = BANDS.map((band, bi) => {
    const ranked = grouped[band.key].sort(
      (a, b) => (b.top?.weight || 0) - (a.top?.weight || 0) || (b.lead.value || 0) - (a.lead.value || 0),
    );
    const take = ranked.slice(0, band.cap);
    const n = take.length;
    shown += n;
    return {
      ...band,
      total: ranked.length,
      overflow: ranked.length - n,
      blips: take.map(({ lead, top }, i) => ({
        id: String(lead._id),
        lead,
        band: band.key,
        r01: Math.sqrt((i + 0.5) / Math.max(n, 1)),
        angleDeg: i * GOLDEN_DEG + bi * 61,
        size01: maxValue > 0 ? Math.sqrt((lead.value || 0) / maxValue) : 0.45,
        severity: top?.severity || null,
        reason: top?.evidence || "Steady — no flags",
        overdue: isOverdue(lead, now),
        idleDays: idleDaysOf(lead, now),
      })),
    };
  });

  return { bands, shown, activeTotal: active.length };
}

const SEV_ORDER = { critical: 3, high: 2, medium: 1, growth: 0 };

/* One prioritized queue of "do this next" items. Each active lead contributes
   at most its single most urgent rule; org advisories ride at the bottom. */
export function buildMissionQueue({ leads = [], meetings = [], leadSources = [], winRate = 0, now, isAdmin = false, fmtValue = (v) => String(v) }) {
  const active = activeOf(leads);
  const maxValue = active.reduce((m, l) => Math.max(m, l.value || 0), 0);
  const items = [];

  for (const l of active) {
    const best = leadRules(l, now, { valNorm: maxValue > 0 ? (l.value || 0) / maxValue : 0, fmtValue })[0];
    if (best) items.push({ ...best, id: `${best.kind}-${l._id}`, leadId: String(l._id) });
  }

  // Meeting prep — anything on the calendar within 24h.
  for (const m of meetings) {
    const at = m.scheduledStart ? new Date(m.scheduledStart).getTime() : 0;
    if (at >= now && at - now <= DAY) {
      const inH = Math.max(1, Math.round((at - now) / 3_600_000));
      items.push({ id: `meeting-${m._id}`, severity: "medium", kind: "meeting", weight: 700 - inH, title: `Prep: ${m.title}`, evidence: `Meeting in ~${inH}h${m.lead?.title ? ` · ${m.lead.title}` : ""}`, to: null, meetUri: m.meetUri || null });
    }
  }

  // Org-level growth advisories.
  const totalSourced = leadSources.reduce((s, x) => s + (x.value || 0), 0);
  const topSource = leadSources.slice().sort((a, b) => (b.value || 0) - (a.value || 0))[0];
  if (topSource && totalSourced >= 10 && topSource.value / totalSourced >= 0.6) {
    items.push({ id: "adv-sources", severity: "growth", kind: "advice", weight: 120, title: "Diversify lead sources", evidence: `${Math.round((topSource.value / totalSourced) * 100)}% of leads come from ${topSource.name}`, to: "leads" });
  }
  const closedish = leads.filter((l) => l.status === "won" || l.status === "lost").length;
  if (closedish >= 4 && winRate < 0.25) {
    items.push({ id: "adv-winrate", severity: "growth", kind: "advice", weight: 110, title: "Win rate is low — audit lost reasons", evidence: `Winning ${Math.round(winRate * 100)}% of closed deals`, to: isAdmin ? "review" : "pipeline" });
  }
  if (active.length > 0 && active.length < 5) {
    items.push({ id: "adv-thin", severity: "growth", kind: "advice", weight: 100, title: "Pipeline is thin — add leads", evidence: `Only ${active.length} active deal${active.length === 1 ? "" : "s"} in play`, to: "leads" });
  }
  if (items.length === 0) {
    items.push({ id: "adv-clear", severity: "growth", kind: "advice", weight: 10, title: active.length ? "Runway clear — go hunt" : "No active deals — add your first lead", evidence: active.length ? "Nothing is overdue or idle. Best use of now: new leads." : "The radar lights up as leads come in.", to: "leads" });
  }

  return items.sort((a, b) => b.weight - a.weight || SEV_ORDER[b.severity] - SEV_ORDER[a.severity]).slice(0, 10);
}

/* 0..100: follow-up discipline (no overdues) + freshness (touched last 7d)
   + win rate. null when there's nothing to score. */
export function healthScore({ leads = [], winRate = 0, now }) {
  const active = activeOf(leads);
  if (active.length === 0) return null;
  const fresh = active.filter((l) => idleDaysOf(l, now) <= 7).length / active.length;
  const discipline = 1 - active.filter((l) => isOverdue(l, now)).length / active.length;
  return Math.max(0, Math.min(100, Math.round(30 * winRate + 40 * fresh + 30 * discipline)));
}

/* Compact snapshot for the AI copilot prompt — plain text, provider-agnostic. */
export function copilotPrompt({ counts, value, winRate, staleLeads, overdueCount, meetings24h, queue, currencyLabel = "" }) {
  const top = (queue || []).slice(0, 5).map((q, i) => `${i + 1}. [${q.severity}] ${q.title} (${q.evidence})`).join("\n");
  return [
    "I'm looking at TeamGrid's built-in CRM module. Snapshot of my sales pipeline right now (this inline snapshot is the actual data — no tools needed, do not disclaim or redirect):",
    `- ${counts.openLeads} active deals, open pipeline ${currencyLabel}${Math.round(value.openValue || 0).toLocaleString()}, win rate ${Math.round((winRate || 0) * 100)}%, ${counts.wonLeads} won / ${counts.lostLeads} lost overall.`,
    `- Idle deals: ${staleLeads.total3} cooling (3-6d), ${staleLeads.total7} stale (7-13d), ${staleLeads.total14} critical (14d+).`,
    `- ${overdueCount} overdue follow-ups, ${meetings24h} meetings in the next 24h.`,
    top ? `- Top signals:\n${top}` : "- No open signals.",
    "",
    "Give me the 3 most important moves to protect and grow revenue right now.",
    "Each line MUST start with exactly one tag — [risk] (revenue at risk), [push] (needs a nudge), or [win] (momentum to reinforce) — then **short action** — why, citing the data above.",
    "One line each, most urgent first. No preamble, no closing.",
  ].filter(Boolean).join("\n");
}
